import CssBaseline from '@mui/material/CssBaseline';
import {
  createTheme as createMuiTheme,
  ThemeProvider,
  useTheme,
  styled,
  alpha,
  type Mixins,
  type Palette,
  type ThemeOptions,
  type Transitions,
  type TypographyVariants as Typography,
  type Shadows,
  type ZIndex,
} from '@mui/material/styles';
import type { Theme as SystemTheme, Shape } from '@mui/system';

import type { BorderRadiusFunction } from './utils';

declare module '@mui/material/styles' {
  interface PaletteOptions {
    type?: string;
    deepPurple?: SimplePaletteColorOptions;
  }

  interface Palette {
    deepPurple: PaletteColor;
  }

  interface SimplePaletteColorOptions {
    lighter?: string;
    darker?: string;
  }

  interface PaletteColor {
    lighter?: string;
    darker?: string;
  }

  interface TypographyVariants {
    body1Semibold: React.CSSProperties;
    body2Semibold: React.CSSProperties;
    captionSemibold: React.CSSProperties;
    captionBold: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    body1Semibold?: React.CSSProperties;
    body2Semibold?: React.CSSProperties;
    captionSemibold?: React.CSSProperties;
    captionBold?: React.CSSProperties;
  }

  interface Theme {
    shape: Shape & {
      rounded: BorderRadiusFunction;
    };
  }

  interface ThemeOptions {
    shape?: Shape & {
      rounded?: BorderRadiusFunction;
    };
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body1Semibold: true;
    body2Semibold: true;
    captionSemibold: true;
    captionBold: true;
  }
}

declare module '@mui/material/Alert' {
  interface AlertPropsColorOverrides {
    muted: true;
  }
}

/*
  Build crash cause builder can not access BaseTheme(private interface) in MUI
  So, re-create BaseTheme and override createTheme as well for allow builder to access their definition
*/
export interface BaseTheme extends SystemTheme {
  mixins: Mixins;
  palette: Palette;
  shadows: Shadows;
  transitions: Transitions;
  typography: Typography;
  zIndex: ZIndex;
  unstable_strictMode?: boolean;
  shape: Shape & {
    rounded: BorderRadiusFunction;
  };
}

export type PartialTheme = Partial<BaseTheme>;

function createTheme(options?: ThemeOptions, ...args: PartialTheme[]): BaseTheme {
  return createMuiTheme(options, ...args) as BaseTheme;
}

export { ThemeProvider, CssBaseline, createTheme, styled, useTheme, alpha };
